import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { getFriendsUseCase, removeFriendUseCase } from '@/src/core/dependencies';
import { FriendshipWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { FriendsScreen } from '@/src/presentation/screens/FriendsScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function FriendsContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [friends, setFriends] = useState<FriendshipWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  useFocusEffect(
    useCallback(() => {
      loadFriends();
    }, [])
  );

  const loadFriends = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getFriendsUseCase.execute(user.id, PAGE_SIZE, 0);
      setFriends(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Friends', 'Failed to load friends', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFriends = async () => {
    if (loadingMore || !hasMore || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getFriendsUseCase.execute(user.id, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setFriends((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Friends', 'Failed to load more friends', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRemoveFriend = async (friendshipId: string, friendName?: string) => {
    platformAlert(
      t('social.removeFriend'),
      t('alerts.confirmRemoveFriend', { name: friendName || t('profile.profile') }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.remove'),
          style: 'destructive',
          onPress: async () => {
            try {
              await removeFriendUseCase.execute(friendshipId);
              setFriends((prev) => prev.filter((f) => f.id !== friendshipId));
              platformAlert(t('common.success'), t('alerts.friendRemoved'));
            } catch (error) {
              logger.error('Friends', 'Failed to remove friend', { error });
              platformAlert(t('common.error'), t('errors.failedToLoad'));
            }
          },
        },
      ]
    );
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  return (
    <FriendsScreen
      friends={friends}
      loading={loading}
      loadingMore={loadingMore}
      currentUserId={user?.id || ''}
      onRemoveFriend={handleRemoveFriend}
      onUserPress={handleUserPress}
      onRefresh={loadFriends}
      onLoadMore={loadMoreFriends}
      colors={colors}
    />
  );
}
